import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, CircularProgress, Alert,
} from '@mui/material';
import { paymentApi } from '../../services/api';

interface AgingRow {
  unitNumber: string;
  ownerName: string;
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
}

const AgingReport: React.FC = () => {
  const [data, setData] = useState<AgingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await paymentApi.getAll();
        const invoices = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const now = new Date();
        const buckets: Record<string, AgingRow> = {};

        invoices.forEach((inv: any) => {
          if (!inv.unitNumber) return;
          if (!buckets[inv.unitNumber]) {
            buckets[inv.unitNumber] = {
              unitNumber: inv.unitNumber,
              ownerName: inv.ownerName || '-',
              current: 0, days1to30: 0, days31to60: 0, days61to90: 0, over90: 0, total: 0,
            };
          }
          const balance = inv.balanceDue || 0;
          if (balance <= 0) return;
          const dueDate = new Date(inv.dueDate);
          const days = Math.floor((now.getTime() - dueDate.getTime()) / 86400000);
          if (days <= 0) buckets[inv.unitNumber].current += balance;
          else if (days <= 30) buckets[inv.unitNumber].days1to30 += balance;
          else if (days <= 60) buckets[inv.unitNumber].days31to60 += balance;
          else if (days <= 90) buckets[inv.unitNumber].days61to90 += balance;
          else buckets[inv.unitNumber].over90 += balance;
          buckets[inv.unitNumber].total += balance;
        });

        setData(Object.values(buckets).sort((a, b) => a.unitNumber.localeCompare(b.unitNumber)));
      } catch { setError('Failed to load aging report.'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const totalCurrent = data.reduce((s, r) => s + r.current, 0);
  const total1to30 = data.reduce((s, r) => s + r.days1to30, 0);
  const total31to60 = data.reduce((s, r) => s + r.days31to60, 0);
  const total61to90 = data.reduce((s, r) => s + r.days61to90, 0);
  const totalOver90 = data.reduce((s, r) => s + r.over90, 0);
  const grandTotal = data.reduce((s, r) => s + r.total, 0);

  const fmt = (v: number) => `RM${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Accounts Receivable Aging</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No outstanding receivables.</Typography></Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Current</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">1-30 Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">31-60 Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">61-90 Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Over 90</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(row => (
                <TableRow key={row.unitNumber} hover>
                  <TableCell>{row.unitNumber}</TableCell>
                  <TableCell>{row.ownerName}</TableCell>
                  <TableCell align="right">{fmt(row.current)}</TableCell>
                  <TableCell align="right">{fmt(row.days1to30)}</TableCell>
                  <TableCell align="right">{fmt(row.days31to60)}</TableCell>
                  <TableCell align="right">{fmt(row.days61to90)}</TableCell>
                  <TableCell align="right">{fmt(row.over90)}</TableCell>
                  <TableCell align="right"><strong>{fmt(row.total)}</strong></TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid' } }}>
                <TableCell colSpan={2}>Totals</TableCell>
                <TableCell align="right">{fmt(totalCurrent)}</TableCell>
                <TableCell align="right">{fmt(total1to30)}</TableCell>
                <TableCell align="right">{fmt(total31to60)}</TableCell>
                <TableCell align="right">{fmt(total61to90)}</TableCell>
                <TableCell align="right">{fmt(totalOver90)}</TableCell>
                <TableCell align="right">{fmt(grandTotal)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AgingReport;
